'use client'

import * as React from 'react'

import { cn } from '../cn'
import { Tabs, TabsList, TabsTrigger } from './tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

export interface OverflowTabItem {
  value: string
  label: React.ReactNode
  disabled?: boolean
  disabledTooltip?: React.ReactNode
  badge?: React.ReactNode
}

export interface OverflowTabsProps {
  items: readonly OverflowTabItem[]
  value: string
  onValueChange: (value: string) => void
  size?: 'sm' | 'md'
  divider?: 'top' | 'bottom' | 'both' | 'none'
  indicator?: 'top' | 'bottom'
  tone?: 'card' | 'transparent'
  wrap?: boolean
  className?: string
  listClassName?: string
  triggerClassName?: string
}

const SIZE_CLASSES = {
  sm: {
    list: 'gap-x-4 gap-y-0',
    trigger: 'pb-2 pt-2 text-[13px]',
    badge: 'text-[10px]',
  },
  md: {
    list: 'gap-x-5 gap-y-0',
    trigger: 'pb-2.5 pt-2.5 text-sm',
    badge: 'text-[11px]',
  },
} satisfies Record<NonNullable<OverflowTabsProps['size']>, Record<string, string>>

const DIVIDER_CLASSES = {
  top: 'border-t border-border',
  bottom: 'border-b border-border',
  both: 'border-y border-border',
  none: '',
} satisfies Record<NonNullable<OverflowTabsProps['divider']>, string>

const INDICATOR_CLASSES = {
  top: '-mt-px border-t-2',
  bottom: '-mb-px border-b-2',
} satisfies Record<NonNullable<OverflowTabsProps['indicator']>, string>

const TONE_CLASSES = {
  card: {
    root: 'bg-card',
    fadeLeft: 'from-card',
    fadeRight: 'from-card',
  },
  transparent: {
    root: 'bg-transparent',
    fadeLeft: 'from-background',
    fadeRight: 'from-background',
  },
} satisfies Record<NonNullable<OverflowTabsProps['tone']>, Record<string, string>>

const triggerBaseClass =
  'inline-flex h-auto items-center gap-1 rounded-none border-transparent bg-transparent px-0.5 font-normal tracking-normal text-muted-foreground shadow-none transition-colors hover:text-foreground focus-visible:ring-inset data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow-none'

const disabledTriggerBaseClass =
  'inline-flex h-auto items-center gap-1 rounded-none border-transparent bg-transparent px-0.5 font-normal tracking-normal text-muted-foreground/50 shadow-none transition-colors hover:text-muted-foreground'

export function OverflowTabs({
  items,
  value,
  onValueChange,
  size = 'md',
  divider = 'bottom',
  indicator = 'bottom',
  tone = 'transparent',
  wrap = true,
  className,
  listClassName,
  triggerClassName,
}: OverflowTabsProps) {
  const scrollerRef = React.useRef<HTMLDivElement>(null)
  const nudgeShownRef = React.useRef(false)
  const nudgeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)
  const [showNudge, setShowNudge] = React.useState(false)

  React.useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) {
      return
    }

    let frame = 0
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')

    const updateScrollAffordance = () => {
      const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1
      setCanScrollLeft(hasOverflow && scroller.scrollLeft > 1)
      setCanScrollRight(hasOverflow && scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1)

      if (hasOverflow && !nudgeShownRef.current) {
        nudgeShownRef.current = true
        if (!reduceMotion?.matches) {
          setShowNudge(true)
          nudgeTimeoutRef.current = setTimeout(() => setShowNudge(false), 900)
        }
      }
    }

    const scheduleUpdate = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(updateScrollAffordance)
    }

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate)
    resizeObserver?.observe(scroller)
    if (scroller.firstElementChild) {
      resizeObserver?.observe(scroller.firstElementChild)
    }
    scroller.addEventListener('scroll', scheduleUpdate, { passive: true })
    window.addEventListener('resize', scheduleUpdate)
    scheduleUpdate()

    return () => {
      cancelAnimationFrame(frame)
      resizeObserver?.disconnect()
      scroller.removeEventListener('scroll', scheduleUpdate)
      window.removeEventListener('resize', scheduleUpdate)
      if (nudgeTimeoutRef.current) {
        clearTimeout(nudgeTimeoutRef.current)
      }
    }
  }, [items.length])

  const sizeClasses = SIZE_CLASSES[size]
  const toneClasses = TONE_CLASSES[tone]
  const indicatorClass = INDICATOR_CLASSES[indicator]
  const tabTriggerClass = cn(triggerBaseClass, indicatorClass, sizeClasses.trigger, triggerClassName)
  const disabledTabClass = cn(disabledTriggerBaseClass, indicatorClass, sizeClasses.trigger, triggerClassName)

  return (
    <Tabs
      value={value}
      onValueChange={onValueChange}
      className={cn(DIVIDER_CLASSES[divider], toneClasses.root, className)}
      data-overflow-tabs>
      <style>{`
        @keyframes ordo-overflow-tabs-nudge {
          0%, 100% { transform: translateX(0); }
          38% { transform: translateX(-14px); }
          68% { transform: translateX(4px); }
        }

        .ordo-overflow-tabs-nudge {
          animation: ordo-overflow-tabs-nudge 850ms ease-out 1;
        }

        @media (prefers-reduced-motion: reduce) {
          .ordo-overflow-tabs-nudge {
            animation: none;
          }
        }
      `}</style>
      <div
        className={cn('relative max-w-full', wrap ? 'overflow-visible' : 'overflow-hidden')}
        data-overflow-tabs-scroll-container>
        <div
          ref={scrollerRef}
          className={cn(
            wrap
              ? 'overflow-visible whitespace-normal'
              : 'overflow-x-auto overflow-y-hidden overscroll-x-contain whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          )}
          data-overflow-tabs-scroller>
          <TabsList
            className={cn(
              'h-auto min-w-full justify-start rounded-none border-0 bg-transparent p-0',
              wrap ? 'flex w-full flex-wrap gap-y-0' : 'w-max flex-nowrap',
              sizeClasses.list,
              showNudge && 'ordo-overflow-tabs-nudge',
              listClassName,
            )}
            data-overflow-tabs-list>
            {items.map((item) =>
              item.disabled ? (
                <DisabledOverflowTab
                  key={item.value}
                  item={item}
                  className={disabledTabClass}
                  badgeClassName={sizeClasses.badge}
                />
              ) : (
                <TabsTrigger key={item.value} value={item.value} className={tabTriggerClass}>
                  {item.label}
                  {item.badge !== undefined ? (
                    <OverflowTabBadge className={sizeClasses.badge}>{item.badge}</OverflowTabBadge>
                  ) : null}
                </TabsTrigger>
              ),
            )}
          </TabsList>
        </div>
        <span
          aria-hidden
          data-overflow-tabs-fade="left"
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r to-transparent transition-opacity duration-150',
            toneClasses.fadeLeft,
            canScrollLeft ? 'opacity-100' : 'opacity-0',
          )}
        />
        <span
          aria-hidden
          data-overflow-tabs-fade="right"
          className={cn(
            'pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l to-transparent transition-opacity duration-150',
            toneClasses.fadeRight,
            canScrollRight ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>
    </Tabs>
  )
}

function DisabledOverflowTab({
  item,
  className,
  badgeClassName,
}: {
  item: OverflowTabItem
  className: string
  badgeClassName: string
}) {
  const button = (
    <button type="button" aria-disabled="true" className={className}>
      {item.label}
      {item.badge !== undefined ? <OverflowTabBadge className={badgeClassName}>{item.badge}</OverflowTabBadge> : null}
    </button>
  )

  if (!item.disabledTooltip) {
    return button
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{item.disabledTooltip}</TooltipContent>
    </Tooltip>
  )
}

function OverflowTabBadge({ children, className }: { children: React.ReactNode; className: string }) {
  return (
    <span
      className={cn(
        'ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 font-semibold leading-none text-muted-foreground',
        className,
      )}>
      {children}
    </span>
  )
}
